import React, { memo, useCallback } from 'react'
import VirtualList from './VirtualList'
import { Droppable, Draggable } from 'react-beautiful-dnd'
import LOG from '../../logging/Logger'
import { LIMIT_USE_VIRTUAL_LISTS } from '../../data/limits'

const getItemStyle = (isDragging, draggableStyle) => ({
  // styles we need to apply on draggables
  ...draggableStyle,
  outline: 'none',

  ...(isDragging && {
    userSelect: 'none',
    cursor: '-webkit-grab; grab'
  }),
  ...(!isDragging && {
    cursor:
      'url(https://www.google.com/intl/en_ALL/mapfiles/openhand.cur) 4 4, move'
  })
})

export default memo(
  ({
    scrollRef,
    renderListItem = {},
    rowCount,
    droppableId,
    isDropDisabled = false,
    isDragDisabled = false,
    estimatedRowSize = 100,
    getRowData,
    disableDragHandle = false,
    children,
    style = {}
  }) => {
    LOG.debug('Render: DNDVirtualList')

    const renderCloneItem = useCallback(
      (provided, snapshot, rubric) => {
        return (
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            style={provided.draggableProps.style}
          >
            {renderListItem({
              index: rubric.source.index,
              dragHandleProps: provided.dragHandleProps,
              isDragging: snapshot.isDragging
            })}
          </div>
        )
      },
      [renderListItem]
    )

    const renderRow = useCallback(
      ({ index }) => {
        const rowData = getRowData(index)
        if (!rowData) {
          return null
        }

        return (
          <Draggable
            draggableId={rowData.id}
            index={index}
            isDragDisabled={isDragDisabled}
            key={rowData.id}
          >
            {(provided, snapshot) => (
              <div
                {...provided.draggableProps}
                {...(!disableDragHandle && provided.dragHandleProps)}
                ref={provided.innerRef}
                style={getItemStyle(
                  snapshot.isDragging,
                  provided.draggableProps.style
                )}
              >
                {renderListItem({
                  index,
                  dragHandleProps: provided.dragHandleProps,
                  isDragging: snapshot.isDragging
                })}
              </div>
            )}
          </Draggable>
        )
      },
      [renderListItem, getRowData, isDragDisabled, disableDragHandle]
    )

    return (
      <Droppable
        mode={rowCount > LIMIT_USE_VIRTUAL_LISTS ? 'virtual' : undefined}
        droppableId={droppableId}
        isDropDisabled={isDropDisabled}
        renderClone={
          rowCount > LIMIT_USE_VIRTUAL_LISTS ? renderCloneItem : undefined
        }
      >
        {(provided, snapshot) => (
          <div style={style}>
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={style}
            >
              {children &&
                children(
                  <VirtualList
                    virtualThreshold={LIMIT_USE_VIRTUAL_LISTS}
                    scrollRef={scrollRef}
                    rowCount={
                      snapshot.isUsingPlaceholder ? rowCount + 1 : rowCount
                    }
                    renderListItem={renderRow}
                    estimatedRowSize={estimatedRowSize}
                    droppableProvided={provided}
                  />,
                  snapshot.isDraggingOver
                )}
              {!children && (
                <VirtualList
                  virtualThreshold={LIMIT_USE_VIRTUAL_LISTS}
                  scrollRef={scrollRef}
                  rowCount={
                    snapshot.isUsingPlaceholder ? rowCount + 1 : rowCount
                  }
                  renderListItem={renderRow}
                  estimatedRowSize={estimatedRowSize}
                  droppableProvided={provided}
                />
              )}
            </div>
          </div>
        )}
      </Droppable>
    )
  }
)
